// Booking detail (from the Bookings list): stay info, folio state, invoice + one-click e-fiscalization.
import React, { useEffect, useState } from 'react';
import { X, Loader2, FileText, CheckCircle2, AlertTriangle, Receipt } from 'lucide-react';
import { API_URL, apiCall, getToken } from './_shared';
import FiscalCell from './FiscalCell';

const eur = (v) => `€${Number(v || 0).toFixed(2)}`;

const Info = ({ label, value, testId }) => (
  <div data-testid={testId}><div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div><div className="text-sm font-semibold text-slate-900">{value || '—'}</div></div>
);

const BookingDetailModal = ({ booking, userRole, onClose }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const id = String(booking.id);

  const load = () => apiCall(`/api/admin/fiscal/bookings/${encodeURIComponent(id)}`).then(setData).catch((e) => setErr(e.message));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const issueInvoice = async () => {
    const amount = data?.folio?.charges_total || booking.total_price;
    if (!window.confirm(`Issue a sequential invoice for ${booking.guest_name} (${eur(amount)})? The number cannot be reused afterwards.`)) return;
    setBusy(true); setErr(''); setOk('');
    try {
      const r = await apiCall(`/api/admin/fiscal/bookings/${encodeURIComponent(id)}/issue-invoice`, { method: 'POST', body: JSON.stringify({ booking }) });
      setOk(`Invoice ${r.invoice_number} issued — now fiscalize it below`); await load();
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  const downloadPdf = async (n) => {
    const r = await fetch(`${API_URL}/api/admin/fiscal/invoices/${encodeURIComponent(n)}/pdf`, { credentials: 'include', headers: { Authorization: `Bearer ${getToken()}` } });
    if (!r.ok) { setErr('PDF failed'); return; }
    const a = document.createElement('a'); a.href = URL.createObjectURL(await r.blob()); a.download = `invoice_${n}.pdf`; a.click();
  };

  const inv = data?.invoice;
  const cancelled = ['cancelled', 'canceled', 'black'].includes(String(booking.status || '').toLowerCase());
  const history = (data?.invoices || []).filter((i) => i.invoice_number !== inv?.invoice_number).map((i) => (
    <div key={i.invoice_number} className="flex items-center justify-between text-xs py-1 border-b last:border-0" data-testid={`booking-invoice-history-${i.invoice_number}`}>
      <span className="font-mono">{i.invoice_number}{i.kind === 'storno' ? ` · storno of ${i.storno_of}` : ''}{i.storno_by ? ` · cancelled by ${i.storno_by}` : ''}</span>
      <span className={i.gross_total < 0 ? 'text-red-600' : ''}>{eur(i.gross_total)}</span>
      <button type="button" onClick={() => downloadPdf(i.invoice_number)} className="text-blue-700 hover:underline">PDF</button>
    </div>
  ));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl p-5 shadow-2xl space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="booking-detail-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900" data-testid="booking-detail-guest">{booking.guest_name}</h3>
            <p className="text-xs text-slate-500">Booking #{id} · {booking.status} · {booking.email || 'no email'}{booking.phone ? ` · ${booking.phone}` : ''}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-slate-100" data-testid="booking-detail-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-50 rounded-lg p-3">
          <Info label="Room" value={booking.room_name} />
          <Info label="Stay" value={`${booking.check_in} → ${booking.check_out}`} />
          <Info label="Guests" value={`${booking.adults}A${booking.children > 0 ? ` + ${booking.children}C` : ''}`} />
          <Info label="Booking total" value={eur(booking.total_price)} testId="booking-detail-total" />
        </div>

        <div className="rounded-xl border p-3 space-y-2" data-testid="booking-fiscal-section">
          <div className="font-semibold text-slate-900 flex items-center gap-2"><Receipt className="w-4 h-4 text-orange-500" /> Invoice &amp; e-Fiscalization</div>
          {!data && !err && <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {data && (
            <>
              <div className="text-xs text-slate-600" data-testid="booking-folio-state">
                {data.folio ? <>Folio: <b>{data.folio.status}</b> · {data.folio.charges} charge lines · {eur(data.folio.charges_total)} charged · {eur(data.folio.payments_total)} paid · balance {eur(data.folio.balance)}</>
                  : <>No folio yet — issuing the invoice will create one with a single accommodation line of <b>{eur(booking.total_price)}</b> from the booking total.</>}
              </div>
              {inv ? (
                <div className="flex flex-wrap items-start gap-4">
                  <div>
                    <div className="text-xs text-slate-500">Invoice</div>
                    <div className="font-mono font-bold" data-testid="booking-invoice-number">{inv.invoice_number}</div>
                    <div className="text-xs text-slate-500">{eur(inv.gross_total)} · issued {(inv.issued_at || '').slice(0, 10)}</div>
                    <button type="button" onClick={() => downloadPdf(inv.invoice_number)} className="mt-1 inline-flex items-center gap-1 text-xs border rounded px-2 py-0.5 hover:bg-slate-50" data-testid="booking-invoice-pdf"><FileText className="w-3 h-3" /> PDF</button>
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="text-xs text-slate-500 mb-0.5">{inv.fiscal?.status === 'fiscalized' ? 'e-Fiscalization' : <><span className="font-bold text-slate-800">Step 2</span> · fiscalize</>}</div>
                    <FiscalCell invoice={inv} onChanged={load} userRole={userRole} />
                  </div>
                </div>
              ) : cancelled ? (
                <div className="text-xs text-slate-500">Cancelled booking — nothing to invoice.</div>
              ) : (
                <div className="flex flex-wrap items-center gap-3">
                  <div className="text-xs text-slate-500"><span className="font-bold text-slate-800">Step 1</span> · issue the invoice</div>
                  <button type="button" onClick={issueInvoice} disabled={busy} className="inline-flex items-center gap-1 bg-orange-500 text-white rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="booking-issue-invoice">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Receipt className="w-4 h-4" />} Issue invoice
                  </button>
                  <div className="text-xs text-slate-400"><span className="font-bold">Step 2</span> · fiscalize (available after the invoice is issued){!data.ready && <span className="text-amber-700 inline-flex items-center gap-1 ml-1"><AlertTriangle className="w-3.5 h-3.5" /> EFI not configured</span>}</div>
                </div>
              )}
              {ok && <div className="text-xs text-emerald-700 inline-flex items-center gap-1" data-testid="booking-fiscal-ok"><CheckCircle2 className="w-3.5 h-3.5" /> {ok}</div>}
              {history.length > 0 && <div className="pt-1"><div className="text-[11px] uppercase text-slate-500">Other invoices for this booking</div>{history}</div>}
            </>
          )}
          {err && <div className="text-xs text-red-600" data-testid="booking-fiscal-error">{err}</div>}
        </div>
        {booking.notes && <div className="text-xs text-slate-600 italic">Notes: {booking.notes}</div>}
      </div>
    </div>
  );
};

export default BookingDetailModal;
